import { createSessionRecord, restoreSessionRecord, hasCreativeWork, knownProjectIds,
  retainCompletedImages, attachRetainedImages, restoreMediaUrls, storageErrorMessage } from './session-snapshot.js';
import { projectsNeedingRecovery, recoverSavedProjects, projectUpdate, mergeRecoveredIteration,
  unidentifiedSubmissions, recoveredSlotProjects, recoveredSensitiveLabels } from './session-recovery.js';
import { restoredGenerationStatus } from './generation-restore.js';

/** Device persistence with explicit account ownership and serialized writes. */
export function createSessionPersistenceController({ state: sessionPersistence, storage, getAccountSnapshot, getSogniClientInstance, onAccountUpdate,
  session, restoreCreativeSession, restart, clearPromptStructure, studioSettingsUI, applyStudioSettings,
  restoreImageReferences, clearImageReferences, generationRun, restoreGenerationRun,
  loadFavoriteVideos, clearLoadedVideos, readSnapshot,
  fetchMedia = async (url) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error('An image could not be saved on this device. Keep this tab open and retry saving.');
    return response.blob();
  } }) {
  let scopeEpoch = 0;
  let scopeStarted = false;
  let lastSnapshot = null;
  let pausedSession = null;
  let pendingSnapshot = null;
  let saveTimer;
  let writePromise = Promise.resolve();
  let lastSavePromise = writePromise;
  let recoveryPromise;
  let recoveryEpoch;
  let recoveredRequestId = null;
  // Sensitive labels from raw synced records: the SDK's replay rebuilds tracked
  // jobs without `nsfwDetected`, so a recovered 'done' update is labelled here.
  let recoveredLabels = new Set();
  const pendingWrites = new Map();
  let stopAccountListener;
  let stopSyncListener;
  const projectListeners = new Map();
  const mediaCache = new Map();
  const retainedImages = new Map();
  const memoryDrafts = new Map();
  let mediaUrls = [];
  let savedProjectSignature = '';

  function captureCreativeSession() { return readSnapshot(sessionPersistence.accountId); }

  function scopeIsCurrent(accountId, epoch = scopeEpoch) {
    const account = getAccountSnapshot();
    return epoch === scopeEpoch && sessionPersistence.accountId === accountId &&
      account.authenticated && account.walletAddress?.toLowerCase() === accountId;
  }

  function stopRecoveryListeners() {
    stopSyncListener?.();
    stopSyncListener = null;
    for (const { stop } of projectListeners.values()) stop();
    projectListeners.clear();
  }

  function releaseMediaUrls() {
    for (const url of mediaUrls) URL.revokeObjectURL(url);
    mediaUrls = [];
    mediaCache.clear();
    retainedImages.clear();
  }

  async function writeSnapshot(snapshot) {
    attachRetainedImages(snapshot, (url) => retainedImages.get(`${snapshot.accountId}:${url}`));
    const first = createSessionRecord(snapshot);
    // Save the request and project IDs before downloading completed image bytes.
    await storage.saveSession(first.record, first.assets);
  }

  async function retainSnapshot(snapshot) {
    await retainCompletedImages(snapshot, async (url) => {
      const key = `${snapshot.accountId}:${url}`;
      if (!mediaCache.has(key)) {
        const download = fetchMedia(url).then((blob) => { retainedImages.set(key, blob); return blob; })
          .catch((error) => { mediaCache.delete(key); throw error; });
        mediaCache.set(key, download);
      }
      return mediaCache.get(key);
    });
    // A media download must never overwrite a newer request or project acknowledgment.
    if (lastSnapshot !== snapshot) return;
    const retained = createSessionRecord(snapshot);
    await storage.saveSession(retained.record, retained.assets);
  }

  function queueSessionSave(snapshot = captureCreativeSession()) {
    if (!sessionPersistence.ready || !snapshot.accountId) return;
    // Start Over must remain empty on reload instead of restoring the previous session.
    const restarted = sessionPersistence.savingPaused
      ? pausedSession?.accountId === snapshot.accountId && pausedSession.id !== snapshot.session.id
      : lastSnapshot?.accountId === snapshot.accountId && lastSnapshot.session.id !== snapshot.session.id;
    if (sessionPersistence.savingPaused) {
      if (!restarted || !scopeIsCurrent(snapshot.accountId)) return;
      sessionPersistence.savingPaused = false;
      pausedSession = null;
    }
    if (!hasCreativeWork(snapshot) && !restarted) return;
    lastSnapshot = snapshot;
    if (sessionPersistence.restoredSessionId !== snapshot.session.id || sessionPersistence.restoredPhase !== snapshot.session.phase) {
      sessionPersistence.restoredSessionId = null;
      sessionPersistence.restoredPhase = null;
    }
    if (recoveredRequestId && snapshot.generationRun?.request?.id !== recoveredRequestId) {
      recoveredRequestId = null;
    }
    memoryDrafts.set(snapshot.accountId, snapshot);
    pendingSnapshot = snapshot;
    clearTimeout(saveTimer);
    const signature = `${snapshot.session.id}:${snapshot.generationRun?.request?.id || ''}:${snapshot.generationRun?.attemptId || ''}:${(snapshot.generationRun?.attemptSlotIds || []).join(',')}:${knownProjectIds(snapshot.generationRun).join(',')}`;
    if (signature !== savedProjectSignature) {
      savedProjectSignature = signature;
      void flushSessionSave();
    } else {
      saveTimer = setTimeout(() => void flushSessionSave(), 400);
    }
  }

  function flushSessionSave({ metadataOnly = false } = {}) {
    clearTimeout(saveTimer);
    const snapshot = pendingSnapshot;
    pendingSnapshot = null;
    if (!snapshot) return metadataOnly ? writePromise : lastSavePromise;
    const epoch = scopeEpoch;
    pendingWrites.set(snapshot.accountId, (pendingWrites.get(snapshot.accountId) || 0) + 1);
    sessionPersistence.isSaving = true;
    writePromise = writePromise.catch(() => {}).then(async () => {
      try {
        await writeSnapshot(snapshot);
        return true;
      } catch (error) {
        if (scopeIsCurrent(snapshot.accountId, epoch)) sessionPersistence.error = storageErrorMessage(error);
        return false;
      }
    });
    // Metadata writes serialize independently of native-image downloads.
    lastSavePromise = writePromise.then(async (written) => {
      if (!written) return false;
      try {
        await retainSnapshot(snapshot);
        if (scopeIsCurrent(snapshot.accountId, epoch) && lastSnapshot === snapshot) {
          sessionPersistence.savedAt = Date.now();
          sessionPersistence.error = null;
        }
        return true;
      } catch (error) {
        if (scopeIsCurrent(snapshot.accountId, epoch) && lastSnapshot === snapshot) sessionPersistence.error = storageErrorMessage(error);
        return false;
      }
    }).finally(() => {
        pendingWrites.set(snapshot.accountId, pendingWrites.get(snapshot.accountId) - 1);
        if (scopeIsCurrent(snapshot.accountId, epoch)) sessionPersistence.isSaving =
          Boolean(pendingSnapshot) || pendingWrites.get(snapshot.accountId) > 0;
    });
    return metadataOnly ? writePromise : lastSavePromise;
  }

  async function saveGenerationAttempt(request) {
    if (!sessionPersistence.ready || !scopeIsCurrent(request.accountId)) {
      const error = new Error('Wait for your signed-in session to finish restoring before generating.');
      error.code = 'SESSION_IDENTITY_CHANGED';
      throw error;
    }
    sessionPersistence.savingPaused = false;
    pausedSession = null;
    queueSessionSave(captureCreativeSession());
    if (await flushSessionSave({ metadataOnly: true }) !== true) {
      const error = new Error(`${sessionPersistence.error || 'This device could not save your session.'} Save your session successfully before generating.`);
      error.code = 'SESSION_SAVE_FAILED';
      throw error;
    }
  }

  function retrySessionSave() {
    sessionPersistence.savingPaused = false;
    pausedSession = null;
    queueSessionSave(captureCreativeSession());
    return flushSessionSave();
  }

  function updateRecoveredProject(projectId, update, accountId, epoch) {
    if (!scopeIsCurrent(accountId, epoch)) return;
    const slot = generationRun.request?.slots.find((item) => generationRun.results[item.id]?.projectId === projectId);
    if (!slot) return;
    if (update.status === 'done' && recoveredLabels.has(projectId)) update = { ...update, sensitive: true };
    const previous = generationRun.results[slot.id] || {};
    // Replaced, not written in place: Studio's live-run effect tracks the results reference.
    generationRun.results = { ...generationRun.results, [slot.id]: { ...slot.projectConfig, slotId: slot.id, model: slot.modelId,
      prompt: slot.prompt, seed: slot.projectConfig.seed, ...previous,
      ...(update.unverified === false ? { error: null, errorCode: null, code: null,
        originalCode: null, errorData: null, errorDetails: null, cause: null } : {}), ...update } };
    if (generationRun.request?.id === recoveredRequestId) {
      mergeRecoveredIteration(session, generationRun);
      generationRun.status = restoredGenerationStatus(generationRun.request, generationRun.results);
    }
  }

  function followProject(project, accountId, epoch) {
    if (projectListeners.get(project.id)?.project === project) return;
    projectListeners.get(project.id)?.stop();
    const apply = () => {
      const slot = generationRun.request?.slots.find((item) => generationRun.results[item.id]?.projectId === project.id);
      if (slot) updateRecoveredProject(project.id, projectUpdate(project, slot.projectConfig), accountId, epoch);
    };
    projectListeners.set(project.id, { project, stop: project.on('updated', apply) });
    apply();
  }

  function reconcileSyncedProjects(synced, client, accountId, epoch) {
    if (!scopeIsCurrent(accountId, epoch)) return [];
    for (const id of recoveredSensitiveLabels(synced)) recoveredLabels.add(id);
    // A completion the SDK replayed before this sync arrived carried no label; apply it now.
    for (const slot of generationRun.request?.slots || []) {
      const result = generationRun.results[slot.id];
      if (result?.status !== 'done' || result.sensitive === true || !recoveredLabels.has(result.projectId)) continue;
      generationRun.results = { ...generationRun.results, [slot.id]: { ...result, sensitive: true } };
      if (generationRun.request?.id === recoveredRequestId) mergeRecoveredIteration(session, generationRun);
    }
    for (const { slotId, projectId } of recoveredSlotProjects(generationRun, synced)) {
      generationRun.results[slotId] = { ...generationRun.results[slotId], projectId };
    }
    const ids = new Set(knownProjectIds(generationRun));
    for (const project of client.projects.trackedProjects) {
      if (ids.has(project.id)) followProject(project, accountId, epoch);
    }
    for (const project of synced.recoveredCompleted || []) {
      if (ids.has(project.id) && project.resultUrls?.[0]) {
        updateRecoveredProject(project.id, { status: 'done', url: project.resultUrls[0], unverified: false }, accountId, epoch);
      }
    }
    return projectsNeedingRecovery(generationRun);
  }

  function followRecoveredProjects(client, accountId, epoch) {
    stopRecoveryListeners();
    recoveredLabels = new Set();
    stopSyncListener = client.projects.on('projectsSynced', (synced) => {
      reconcileSyncedProjects(synced, client, accountId, epoch);
    });
  }

  function refreshSavedProjects() {
    const accountId = sessionPersistence.accountId;
    const epoch = scopeEpoch;
    if (recoveryPromise && recoveryEpoch === epoch) return recoveryPromise;
    const client = getSogniClientInstance();
    if (!client || !scopeIsCurrent(accountId, epoch)) return Promise.resolve();
    const projectIds = projectsNeedingRecovery(generationRun);
    if (!projectIds.length && !unidentifiedSubmissions(generationRun).length) return Promise.resolve();
    sessionPersistence.isRecovering = true;
    sessionPersistence.recoveryError = null;
    recoveryEpoch = epoch;
    const promise = recoverSavedProjects({ projects: client.projects, projectIds,
      isCurrent: () => scopeIsCurrent(accountId, epoch),
      onSync: (synced) => reconcileSyncedProjects(synced, client, accountId, epoch),
      onProject: (project) => followProject(project, accountId, epoch),
      onUpdate: (id, update) => updateRecoveredProject(id, update, accountId, epoch)
    }).catch((error) => {
      if (scopeIsCurrent(accountId, epoch)) sessionPersistence.recoveryError =
        error.message || 'Existing projects could not be verified. Keep your work and check again.';
    }).finally(() => {
      if (scopeIsCurrent(accountId, epoch)) sessionPersistence.isRecovering = false;
      if (recoveryPromise === promise) recoveryPromise = null;
    });
    recoveryPromise = promise;
    return recoveryPromise;
  }

  function installSnapshot(snapshot) {
    releaseMediaUrls();
    mediaUrls = restoreMediaUrls(snapshot, (blob) => URL.createObjectURL(blob));
    restoreCreativeSession(snapshot);
    if (snapshot.settings) applyStudioSettings(snapshot.settings);
    studioSettingsUI.pauseInitialGeneration = true;
    studioSettingsUI.autoOpenedForSession = true;
    restoreImageReferences(snapshot.references || { assets: [], guideImage: null });
    restoreGenerationRun(snapshot.generationRun || null);
    sessionPersistence.restoredSessionId = session.id;
    sessionPersistence.restoredPhase = session.phase;
    recoveredRequestId = generationRun.request?.id || null;
    mergeRecoveredIteration(session, generationRun);
  }

  async function restoreSavedSession(accountId = sessionPersistence.accountId, id) {
    const epoch = scopeEpoch;
    if (!scopeIsCurrent(accountId, epoch)) return false;
    sessionPersistence.ready = false;
    try {
      if (await flushSessionSave() === false) return false;
      const saved = await storage.loadSession(accountId, id);
      if (!scopeIsCurrent(accountId, epoch) || !saved) return false;
      const snapshot = restoreSessionRecord(saved.record, saved.assets, accountId);
      installSnapshot(snapshot);
      sessionPersistence.savedAt = saved.record.updatedAt;
      sessionPersistence.error = null;
      sessionPersistence.savingPaused = false;
      pausedSession = null;
      const client = getSogniClientInstance();
      if (client) followRecoveredProjects(client, accountId, epoch);
      await refreshSavedProjects();
      return true;
    } catch (error) {
      if (scopeIsCurrent(accountId, epoch)) sessionPersistence.error = storageErrorMessage(error);
      return false;
    } finally {
      if (scopeIsCurrent(accountId, epoch)) sessionPersistence.ready = true;
    }
  }

  async function switchAccount(accountId) {
    if (scopeStarted && accountId === sessionPersistence.accountId) return;
    scopeStarted = true;
    const previous = sessionPersistence.accountId;
    const guestDraft = !previous && hasCreativeWork(captureCreativeSession()) ? captureCreativeSession() : null;
    if (lastSnapshot?.accountId === previous && previous) memoryDrafts.set(previous, lastSnapshot);
    void flushSessionSave();
    const epoch = ++scopeEpoch;
    sessionPersistence.accountId = accountId;
    sessionPersistence.ready = false;
    sessionPersistence.savedAt = null;
    sessionPersistence.restoredSessionId = null;
    sessionPersistence.restoredPhase = null;
    sessionPersistence.error = null;
    sessionPersistence.recoveryError = null;
    sessionPersistence.sessions = [];
    sessionPersistence.savingPaused = false;
    pausedSession = null;
    sessionPersistence.isSaving = false;
    sessionPersistence.isRecovering = false;
    savedProjectSignature = '';
    lastSnapshot = null;
    stopRecoveryListeners();
    releaseMediaUrls();
    clearLoadedVideos();
    if (previous) {
      restart();
      clearPromptStructure();
      clearImageReferences();
      restoreGenerationRun(null);
    }
    if (!accountId) {
      sessionPersistence.ready = true;
      return;
    }
    try {
      const draft = memoryDrafts.get(accountId) || guestDraft;
      if (draft) {
        installSnapshot({ ...draft, accountId });
      } else {
        const saved = await storage.loadSession(accountId);
        if (!scopeIsCurrent(accountId, epoch)) return;
        if (saved) {
          installSnapshot(restoreSessionRecord(saved.record, saved.assets, accountId));
          sessionPersistence.savedAt = saved.record.updatedAt;
        }
      }
      if (!scopeIsCurrent(accountId, epoch)) return;
      await loadFavoriteVideos(accountId);
      if (!scopeIsCurrent(accountId, epoch)) return;
      const client = getSogniClientInstance();
      if (client) followRecoveredProjects(client, accountId, epoch);
      await refreshSavedProjects();
    } catch (error) {
      if (scopeIsCurrent(accountId, epoch)) sessionPersistence.error = storageErrorMessage(error);
    } finally {
      if (scopeIsCurrent(accountId, epoch)) sessionPersistence.ready = true;
    }
  }

  function startSessionPersistence() {
    const applyAccount = (snapshot) => {
      void switchAccount(snapshot.authenticated ? snapshot.walletAddress?.toLowerCase() || null : null);
    };
    stopAccountListener = onAccountUpdate(applyAccount);
    applyAccount(getAccountSnapshot());
    const stopVersionChange = storage.onStorageVersionChange((message) => { sessionPersistence.error = message; });
    return () => {
      void flushSessionSave();
      stopAccountListener?.();
      stopVersionChange();
      stopRecoveryListeners();
      releaseMediaUrls();
      scopeStarted = false;
    };
  }

  async function refreshSessionStorage(limit = 50) {
    const accountId = sessionPersistence.accountId;
    const epoch = scopeEpoch;
    if (!scopeIsCurrent(accountId, epoch)) return;
    try {
      const sessions = await storage.listSessions(accountId, limit);
      const estimate = await navigator.storage?.estimate?.();
      const persisted = await navigator.storage?.persisted?.();
      if (!scopeIsCurrent(accountId, epoch)) return;
      sessionPersistence.sessions = sessions;
      sessionPersistence.storageUsage = estimate?.usage ?? null;
      sessionPersistence.storageQuota = estimate?.quota ?? null;
      sessionPersistence.persistentStorage = persisted ?? null;
    } catch (error) {
      if (scopeIsCurrent(accountId, epoch)) sessionPersistence.error = storageErrorMessage(error);
    }
  }

  async function forgetSavedSession(id) {
    const accountId = sessionPersistence.accountId;
    const epoch = scopeEpoch;
    if (!scopeIsCurrent(accountId, epoch)) return;
    try {
      // Deletion must still free space when the current draft could not be saved.
      await flushSessionSave();
      if (!scopeIsCurrent(accountId, epoch)) return;
      await storage.deleteSession(accountId, id);
      if (!scopeIsCurrent(accountId, epoch)) return;
      if (id === session.id) {
        sessionPersistence.savingPaused = true;
        pausedSession = { accountId, id };
        sessionPersistence.savedAt = null;
        memoryDrafts.delete(accountId);
      }
      await refreshSessionStorage();
    } catch (error) {
      if (scopeIsCurrent(accountId, epoch)) sessionPersistence.error = storageErrorMessage(error);
    }
  }

  return { captureCreativeSession, queueSessionSave, flushSessionSave, saveGenerationAttempt, retrySessionSave, refreshSavedProjects, restoreSavedSession, startSessionPersistence, refreshSessionStorage, forgetSavedSession };
}
